import { useEffect, useMemo, useRef, type RefObject } from "react"
import { Formik, getIn, useFormikContext } from "formik"
import { CharField, SingleSelectField, Button } from "@inmagik/magik-ui"
import * as yup from "yup"
import type { BlockBase, Media, MediaItem, TwoImagesBlock, MediaFit } from "../../../../types"
import { MediaThumb } from "../Previews"
import { mediaFitOptions, mediaPositionOptions } from "../../options"
import { AutoValidate } from "./Autovalidate"
import { WysiwygField } from "../../../../components/WysiwygField"
import { MediaSelectionModal } from "../MediaSelectionModal"

function ensureTwo(items: MediaItem[] | undefined): [MediaItem, MediaItem] {
  const base: MediaItem[] = Array.isArray(items) ? items : []
  const a = base[0] ?? {}
  const b = base[1] ?? {}
  return [
    {
      id: a.id,
      title: a.title ?? "",
      thumbnail: a.thumbnail ?? "",
      media_file: a.media_file ?? "",
      caption: a.caption ?? "",
      fit: a.fit ?? "fit",
      position: a.position ?? "center center",
    },
    {
      id: b.id,
      title: b.title ?? "",
      thumbnail: b.thumbnail ?? "",
      media_file: b.media_file ?? "",
      caption: b.caption ?? "",
      fit: b.fit ?? "fit",
      position: b.position ?? "center center",
    },
  ]
}

function TwoMediaPicker() {
  const { values, setFieldValue } = useFormikContext<{ items: [MediaItem, MediaItem] }>()

  const items: [MediaItem, MediaItem] = useMemo(() => ensureTwo(values.items), [values.items])
  const selectedIds = items.map((x) => x.id).filter(Boolean) as number[]

  const removeIfAlreadySelected = (id: number): [MediaItem, MediaItem] => {
    const next = ensureTwo(values.items)
    const idx = next.findIndex((x) => x.id === id)
    if (idx !== -1) next[idx] = { ...next[idx], id: undefined, title: "", thumbnail: "", media_file: "" } as MediaItem
    return next
  }

  const pickForSlot = (slot: 0 | 1, doc: Media) => {
    const id = doc.id as number
    const next = removeIfAlreadySelected(id)
    next[slot] = {
      ...next[slot],
      id,
      title: doc.title ?? "",
      thumbnail: String(doc.thumbnail ?? doc.media_file ?? ""),
      media_file: String(doc.media_file ?? ""),
    }
    setFieldValue("items", next)
  }

  const selectNewMedia = (doc: Media) => {
    const id = doc.id as number
    const next = removeIfAlreadySelected(id)

    if (!next[0].id) {
      next[0] = {
        ...next[0],
        id,
        title: doc.title ?? "",
        thumbnail: String(doc.thumbnail ?? doc.media_file ?? ""),
        media_file: String(doc.media_file ?? ""),
        position: "center center",
      }
    } else {
      next[1] = {
        ...next[1],
        id,
        title: doc.title ?? "",
        thumbnail: String(doc.thumbnail ?? doc.media_file ?? ""),
        media_file: String(doc.media_file ?? ""),
        position: "center center",
      }
    }

    setFieldValue("items", next)
  }

  return (
    <MediaSelectionModal
      buttonLabel="Seleziona immagini"
      title="Seleziona immagini"
      selectionSummary={`${selectedIds.length} / 2 immagini selezionate.`}
      renderSelectionCell={(doc) => (
        <div className="d-flex gap-2">
          <Button
            size="sm"
            title="Prima"
            onClick={() => pickForSlot(0, doc)}
            variant="primary"
            outline={items[0]?.id !== doc.id}
          />
          <Button
            size="sm"
            title="Seconda"
            onClick={() => pickForSlot(1, doc)}
            variant="primary"
            outline={items[1]?.id !== doc.id}
          />
        </div>
      )}
      onCreateAndSelect={selectNewMedia}
    />
  )
}

export function TwoImagesBlockForm({
  block,
  saveBlock,
  setBlockFormValid,
  submitRef,
}: {
  block: BlockBase
  saveBlock: (b: BlockBase) => void
  setBlockFormValid: (v: boolean) => void
  submitRef: RefObject<HTMLButtonElement | null>
}) {
  const schema = yup.object().shape({
    slideTitle: yup.string().required("Il titolo della slide è obbligatorio"),
    items: yup
      .array()
      .of(
        yup.object().shape({
          id: yup.number().required("Seleziona un'immagine"),
          caption: yup.string().nullable(),
          fit: yup.string().oneOf(["fit", "cover"]).required("Fit obbligatorio"),
        }),
      )
      .test("two", "Seleziona 2 immagini", (arr: any) => Array.isArray(arr) && !!arr[0]?.id && !!arr[1]?.id)
      .required(),
  })

  const hiddenSubmitRef = useRef<HTMLButtonElement>(null)
  useEffect(() => {
    if (!submitRef?.current) return
    submitRef.current.onclick = () => hiddenSubmitRef.current?.click()
  }, [submitRef])

  const initialValues: TwoImagesBlock = {
    uuid: block.uuid,
    blockType: "TwoImages",
    slideTitle: (block as TwoImagesBlock).slideTitle ?? "",
    items: ensureTwo((block as TwoImagesBlock).items),
  }

  return (
    <Formik
      enableReinitialize
      validateOnMount
      initialValues={initialValues}
      validationSchema={schema}
      onSubmit={(values) => saveBlock(values)}
    >
      {({ handleSubmit, values, errors, setFieldValue }) => {
        const items = ensureTwo(values.items)

        const setItem = (idx: 0 | 1, patch: Partial<MediaItem>) => {
          const next = ensureTwo(values.items)
          next[idx] = { ...next[idx], ...patch }
          setFieldValue("items", next)
        }

        const removeItem = (idx: 0 | 1) => {
          const next = ensureTwo(values.items)
          next[idx] = { id: undefined, title: "", thumbnail: "", media_file: "", caption: "", fit: "fit" }
          setFieldValue("items", next)
        }

        return (
          <form onSubmit={handleSubmit}>
            <CharField
              label="Titolo slide"
              layout="horizontal"
              value={values.slideTitle}
              onChange={(v) => setFieldValue("slideTitle", v)}
              required
              isValid={!getIn(errors, "slideTitle")}
              errorMessage={getIn(errors, "slideTitle")}
              className="mb-3"
            />

            <div className="mb-3 d-flex align-items-center flex-column gap-2 mb-3 w-100">
              <TwoMediaPicker />
            </div>

            <div className="mb-3">
              {[0, 1].map((i) => {
                const it = items[i] || {}
                const idx = i as 0 | 1

                return (
                  <div
                    className="mb-2"
                    key={it.id ?? idx}
                    style={{
                      padding: 12,
                      border: "1px solid rgba(0,0,0,.08)",
                      borderRadius: 10,
                      background: "#f2f2f2",
                      boxShadow: "0 1px 3px rgba(0,0,0,.1)",
                    }}
                  >
                    <div className="w-100">
                      <div className="d-flex align-items-center flex-column justify-content-center gap-3 w-100 mb-3">
                        <MediaThumb
                          thumbnail={it.thumbnail}
                          title={it.title}
                          fit={it.fit}
                          width={250}
                          height={170}
                          position={it.position}
                        />

                        <div className="w-100 d-flex justify-content-center align-items-center">
                          <div style={{ fontSize: 13 }}>
                            <strong>ID:</strong> {it.id ?? <span className="text-muted">—</span>}
                            {it.title ? <span className="text-muted"> — {it.title}</span> : null}
                          </div>

                          <button
                            type="button"
                            className="btn btn-outline-danger btn-sm ms-3"
                            onClick={() => removeItem(idx)}
                          >
                            Rimuovi
                          </button>
                        </div>
                      </div>

                      <div className="mb-2">
                        <SingleSelectField
                          label="Fit"
                          layout="horizontal"
                          options={mediaFitOptions}
                          value={mediaFitOptions.find((o) => o.value === (it.fit ?? "fit"))}
                          onChange={(opt) => opt && setItem(idx, { fit: opt.value as MediaFit })}
                        />
                      </div>

                      {it.fit === "cover" && (
                        <div className="mb-2">
                          <SingleSelectField
                            label="Posizione immagine"
                            layout="horizontal"
                            options={mediaPositionOptions}
                            value={mediaPositionOptions.find((o) => o.value === it.position)}
                            onChange={(opt) => opt && setItem(idx, { position: opt.value })}
                          />
                        </div>
                      )}
                      <WysiwygField
                        label="Didascalia"
                        layout="horizontal"
                        value={it.caption || ""}
                        onChange={(v) => setItem(idx, { caption: v })}
                        className="mb-2"
                        rows={6}
                        placeholder="(opzionale)"
                      />
                    </div>
                  </div>
                )
              })}
            </div>

            {getIn(errors, "items") ? (
              <div className="text-danger mb-3" style={{ fontSize: 13 }}>
                {typeof getIn(errors, "items") === "string" ? String(getIn(errors, "items")) : "Controlla i campi"}
              </div>
            ) : null}

            <button ref={hiddenSubmitRef} type="submit" style={{ display: "none" }} />
            <AutoValidate setBlockFormValid={setBlockFormValid} />
          </form>
        )
      }}
    </Formik>
  )
}
